import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ProjectOverviewCard } from './Cards/ProjectOverviewCard';
import { ContractorAnalysisCard } from './Cards/ContractorAnalysisCard';
import { WorkerTimesheetCard } from './Cards/WorkerTimesheetCard';
import { IncidentReportCard } from './Cards/IncidentReportCard';
import { DailyRouteCard } from './Cards/DailyRouteCard';
import { ObserveLocationCard } from './Cards/ObserveLocationCard';
import { ScissorLiftExportCard } from './Cards/ScissorLiftExportCard';
import { RecentReportsRail } from './RecentReports/RecentReportsRail';
import { SmartSearchPlaceholder } from './SmartSearchPlaceholder';
import {
  createInvestigationReport,
  listInvestigationReports,
  reportRoute,
} from '@apis/investigationReports';
import { selectSiteTz } from '@store/selectors/site.selectors';
import type { IReportType } from './analytics.types';
import {
  APP_DEFAULT_TZ,
  pickerDateTimeToProjectMs,
  pickerDateToProjectNoonMs,
} from '@utils/dateUtils';
import { localDateToYmd } from './utils/dateRules';
import styles from './styles.module.css';

type SelectableName = IReportType | 'scissorLiftExport';

/**
 * The recent rail lists the site's archived `IInvestigationReport` records
 * from `listInvestigationReports`.
 */
export function AnalyticsPage() {
  const { siteId = '' } = useParams<{ siteId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const tz = useSelector(selectSiteTz) ?? APP_DEFAULT_TZ;
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<SelectableName | null>(null);

  // Persisted submissions live in trusstor's archive (proxied via
  // /api/sites/:siteId/reports). The query is keyed by siteId so navigating
  // between sites refetches the right list.
  const recentQuery = useQuery({
    queryKey: ['investigationReports', siteId] as const,
    queryFn: ({ signal }) => listInvestigationReports(siteId, signal),
    enabled: !!siteId,
    staleTime: 30_000,
  });

  // Submit mutation. Currently only Daily Route persists end-to-end; the
  // other cards' submit handlers are no-ops until their report pages
  // exist. On success we invalidate the list query so the new entry
  // appears in the recent rail when the user navigates back.
  const createMutation = useMutation({
    mutationFn: (input: Parameters<typeof createInvestigationReport>[1]) =>
      createInvestigationReport(siteId, input),
    onSuccess: ({ reportId }, variables) => {
      void queryClient.invalidateQueries({
        queryKey: ['investigationReports', siteId],
      });
      navigate(reportRoute(siteId, variables.investigationType, reportId));
    },
  });

  return (
    <div className={styles.page}>
      <section className={styles.cardsSection}>
        <div className={styles.cardsInner}>
          <SmartSearchPlaceholder />
          <div className={styles.grid}>
            <ScissorLiftExportCard
              isSelected={selected === 'scissorLiftExport'}
              onSelect={setSelected}
            />
            {/* Non-Daily-Route cards are scaffolded but not persisted yet
                (their report pages don't exist). Submit handlers stay
                inert until those pages get built — see the matching
                ComingSoon entries in routes.tsx. */}
            <ProjectOverviewCard
              isSelected={selected === 'projectOverview'}
              onSelect={setSelected}
              onSubmit={(submission) => {
                const isPeriod = submission.mode === 'period';
                // Guard the impossible-by-UI path (Create is gated on the
                // date/range being fully picked, but TS can't see that).
                const from = isPeriod ? submission.range.from : submission.date;
                if (!from) return;
                if (isPeriod && !submission.range.to) return;
                createMutation.mutate({
                  investigationType: 'projectOverview',
                  title: t('reports.projectOverview.title'),
                  // Empty on purpose — matches trusstor's archive shape so the
                  // record round-trips. The header falls back to the localized
                  // default subtitle when description is empty.
                  description: '',
                  // No `investigatedTimestamp`: this report's subject is the
                  // window below, not an instant. `Date.now()` here only ever
                  // restated `dateGenerated` as if it were the analysed date.
                  periodPickerMode: isPeriod ? 'PERIOD' : 'DATE',
                  dateFrom: localDateToYmd(from),
                  ...(isPeriod && submission.range.to
                    ? { dateTo: localDateToYmd(submission.range.to) }
                    : {}),
                });
              }}
            />
            <ContractorAnalysisCard
              isSelected={selected === 'contractorAnalysis'}
              onSelect={setSelected}
              onSubmit={(submission) => {
                createMutation.mutate({
                  investigationType: 'contractorAnalysis',
                  title: t('reports.contractorAnalysis.titleFallback'),
                  description: t('reports.contractorAnalysis.subtitle'),
                  // No `investigatedTimestamp`: a contractor analysis is
                  // keyed on the profession and spans all time — there is no
                  // date it is "about" to record.
                  professionId: submission.professionId,
                  professionLabel: submission.professionLabel,
                });
              }}
            />
            <WorkerTimesheetCard
              isSelected={selected === 'workersTimesheet'}
              onSelect={setSelected}
              onSubmit={(submission) => {
                // Guard against the impossible-by-UI path where the
                // submit button fires before the range is fully picked.
                // The card already gates Create on `range.from`/`range.to`
                // being non-null, but TS doesn't see through that.
                if (!submission.range.from || !submission.range.to) return;
                createMutation.mutate({
                  investigationType: 'workersTimesheet',
                  title: t('reports.workersTimesheet.title'),
                  description: t('reports.workersTimesheet.subtitle'),
                  // No `investigatedTimestamp`: the subject is the
                  // `dateFrom`–`dateTo` window below. Pinning the range's
                  // first day into a second, differently-derived field only
                  // created a value that could disagree with the window.
                  tagIds: submission.tagIds,
                  // `localDateToYmd`, NOT `formatYmd(_, tz)`. The range picker
                  // emits tz-naive Dates carrying the clicked calendar day in
                  // local fields (see `ymdToLocalDate`/`localDateToYmd` in
                  // utils/dateRules, and DateRangePicker which commits with the
                  // same pair). `formatYmd` re-reads that Date as an *instant*
                  // and converts it into the project tz, which lands on the
                  // previous day whenever the browser is ahead of the site —
                  // e.g. midnight on the 8th in UTC+3 is 17:00 on the 7th in
                  // UTC-4, so a range picked as "the 8th" was stored as "the
                  // 7th". `projectOverview` above never had the bug because it
                  // always used the tz-naive helper.
                  dateFrom: localDateToYmd(submission.range.from),
                  dateTo: localDateToYmd(submission.range.to),
                });
              }}
            />
            <IncidentReportCard
              isSelected={selected === 'incidentReport'}
              onSelect={setSelected}
              onSubmit={(submission) =>
                createMutation.mutate({
                  investigationType: 'incidentReport',
                  // The DateTimeField follows the same tz-naive convention
                  // as DateField: its emitted Date holds the picked
                  // y/m/d/h/m in *local* fields, by convention representing
                  // the project-tz calendar fields. Re-anchor in project tz
                  // here so the archived instant matches what the user
                  // actually picked (a JST clock time) regardless of where
                  // they're viewing from.
                  investigatedTimestamp: pickerDateTimeToProjectMs(
                    submission.at,
                    tz,
                  ),
                  title: t('reports.incidentReport.title'),
                  description: t('reports.incidentReport.subtitle'),
                  entityType: submission.entityKind,
                  ...(submission.entityKind === 'tag'
                    ? {
                        tagId: submission.entityId,
                        tagNick: submission.entityNick,
                        tagName: submission.entityName,
                      }
                    : {
                        equipmentId: submission.entityId,
                        equipmentName: submission.entityName,
                      }),
                })
              }
            />
            <DailyRouteCard
              isSelected={selected === 'dailyRoute'}
              onSelect={setSelected}
              onSubmit={(submission) =>
                createMutation.mutate({
                  investigationType: 'dailyRoute',
                  // Default title / description — the user can rename on
                  // the report page. Keep these short and recognisable so
                  // the recent rail's prominent line is informative
                  // before any rename happens.
                  title: t('reports.dailyRoute.title'),
                  description: t('reports.dailyRoute.subtitle'),
                  // Pin the submission to project-tz noon of the picked
                  // day so the archived instant lands on the user's
                  // intended calendar day regardless of where they're
                  // viewing from (and survives DST).
                  investigatedTimestamp: pickerDateToProjectNoonMs(
                    submission.date,
                    tz,
                  ),
                  entityType: submission.entityKind,
                  ...(submission.entityKind === 'tag'
                    ? {
                        tagId: submission.entityId,
                        tagNick: submission.entityNick,
                        tagName: submission.entityName,
                      }
                    : {
                        equipmentId: submission.entityId,
                        equipmentName: submission.entityName,
                      }),
                })
              }
            />
            <ObserveLocationCard
              isSelected={selected === 'observeLocation'}
              onSelect={setSelected}
              onSubmit={(submission) =>
                createMutation.mutate({
                  investigationType: 'observeLocation',
                  title: t('reports.observeLocation.title'),
                  description: t('reports.observeLocation.subtitle'),
                  investigatedTimestamp: pickerDateToProjectNoonMs(submission.date, tz),
                  location: submission.locations,
                })
              }
            />
          </div>
        </div>
      </section>

      <section className={styles.recentSection}>
        <div className={styles.recentInner}>
          <RecentReportsRail items={recentQuery.data ?? []} siteId={siteId} />
        </div>
      </section>
    </div>
  );
}
